import { Link } from "react-router-dom";
import {
  TrendingUp,
  LayoutDashboard,
  Play,
  FolderArchive,
  Plug,
  Settings,
  BarChart3,
  Sparkles,
  DollarSign,
  DatabaseZap,
  UploadCloud,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  MapPin,
  ListOrdered,
  Home,
  Building2,
  Globe,
} from "lucide-react";
import { cn } from "@repo/ui/lib/utils";
import { Button } from "@repo/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  currentPath: string;
}

const navItems = [
  { path: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { path: "/dashboard/jobs", label: "Scrape Jobs", icon: Play },
  { path: "/dashboard/queue", label: "Queue & Monitor", icon: ListOrdered },
  { path: "/dashboard/properties", label: "Properties", icon: Home },
  { path: "/dashboard/agencies", label: "Agencies", icon: Building2 },
  { path: "/dashboard/suburbs", label: "Suburbs", icon: MapPin },
  { path: "/dashboard/adapters", label: "Adapters", icon: Plug },
  { path: "/dashboard/suburb-adapters", label: "Suburb Adapters", icon: Globe },
  { path: "/dashboard/exports", label: "Exports", icon: FolderArchive },
  { path: "/dashboard/analysis", label: "Analysis", icon: BarChart3 },
  { path: "/dashboard/ai", label: "AI", icon: Sparkles },
  { path: "/dashboard/poisoned-data", label: "Poisoned Data", icon: ShieldAlert },
  { path: "/dashboard/data-connections", label: "Data Connections", icon: DatabaseZap },
  { path: "/dashboard/data-ingress", label: "Data Ingress", icon: UploadCloud },
  { path: "/dashboard/valuations", label: "Valuations", icon: DollarSign },
  { path: "/dashboard/settings", label: "Settings", icon: Settings },
];

export const Sidebar = ({ collapsed, onToggle, currentPath }: SidebarProps) => {
  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-full bg-sidebar border-r border-sidebar-border z-40",
        "flex flex-col transition-all duration-300",
        "hidden lg:flex",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-sidebar-border">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-sidebar-primary flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-sidebar-primary-foreground" />
          </div>
          {!collapsed && (
            <span className="text-lg font-semibold text-sidebar-foreground">
              EquityStart
            </span>
          )}
        </Link>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="text-sidebar-foreground hover:bg-sidebar-accent h-8 w-8"
        >
          {collapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1">
        {navItems.map((item) => {
          const isActive = currentPath === item.path || 
            (item.path !== "/dashboard" && currentPath.startsWith(item.path));
          
          const linkContent = (
            <Link
              to={item.path}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                "text-sidebar-foreground/80 hover:text-sidebar-foreground",
                isActive 
                  ? "bg-sidebar-accent text-sidebar-accent-foreground" 
                  : "hover:bg-sidebar-accent/50"
              )}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && (
                <span className="text-sm font-medium">{item.label}</span>
              )}
            </Link>
          );

          if (collapsed) {
            return (
              <Tooltip key={item.path} delayDuration={0}>
                <TooltipTrigger asChild>
                  {linkContent}
                </TooltipTrigger>
                <TooltipContent side="right" className="ml-2">
                  {item.label}
                </TooltipContent>
              </Tooltip>
            );
          }

          return <div key={item.path}>{linkContent}</div>;
        })}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="p-4 border-t border-sidebar-border">
          <p className="text-xs text-sidebar-foreground/50">
            EquityStart Ops v1.0
          </p>
        </div>
      )}
    </aside>
  );
};
